import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Heart, Fingerprint, Cog } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Advantages = () => {
  const { t } = useLanguage();

  const advantages = t('advantages.items');
  const icons = [Sparkles, Heart, Fingerprint, Cog];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section 
      id="advantages" 
      data-testid="advantages-section"
      className="py-24 md:py-32 relative overflow-hidden bg-[#0A0A0A]"
    >
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#FF7A00]/5 rounded-full blur-[150px] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-[#FF7A00]/10 text-[#FF7A00] text-sm tracking-widest uppercase mb-4">
              Advantages
            </span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              {t('advantages.title')}
            </h2>
            
            {/* Advantages List */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-6"
            >
              {advantages.map((advantage, index) => {
                const Icon = icons[index];
                return (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    data-testid={`advantage-${index}`}
                    className="flex gap-4 group"
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#FF7A00]/10 border border-[#FF7A00]/20 flex items-center justify-center group-hover:bg-[#FF7A00]/20 transition-colors">
                      <Icon className="w-6 h-6 text-[#FF7A00]" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-1 group-hover:text-[#FF7A00] transition-colors">
                        {advantage.title}
                      </h4>
                      <p className="text-white/60 text-sm leading-relaxed">
                        {advantage.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>

          {/* Right - Visual */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            {/* Image Container */}
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5]">
              <img
                src="https://customer-assets.emergentagent.com/job_ifotolife-popup/artifacts/ylz58tgj_ChatGPT%20Image%203%20fe%CC%81vr.%202026%2C%2012_47_58.png"
                alt="Stand iFotoLife avec photobooth et photographes"
                className="w-full h-full object-cover object-top"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              
              {/* iFotoLife Branding Banner */}
              <div className="absolute top-6 left-6 right-6">
                <div className="glass rounded-xl p-3 flex items-center justify-center gap-3">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_ifotolife-popup/artifacts/aoncqacu_ChatGPT%20Image%2028%20janv.%202026%2C%2014_08_19.png" 
                    alt="iFotoLife Logo" 
                    className="h-8 w-auto"
                  />
                  <span className="text-white font-bold text-lg tracking-wide">Pop-up Photo</span>
                </div>
              </div>
              
              {/* Stats Overlay */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="glass rounded-2xl p-6">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-[#FF7A00]">+500</div>
                      <div className="text-xs text-white/60">Photos/jour</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-[#FF7A00]">98%</div>
                      <div className="text-xs text-white/60">Satisfaction</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-[#FF7A00]">24/7</div>
                      <div className="text-xs text-white/60">Support</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="absolute -top-6 -right-6 glass-card rounded-2xl p-4 shadow-xl"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#10B981]/10 flex items-center justify-center">
                  <span className="text-[#10B981] text-lg">✓</span>
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">Zéro Effort</div>
                  <div className="text-xs text-white/50">100% Automatisé</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Advantages;
